import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import client from "../lib/api";
import { mediaSrc, mediaKind, formatBytes, uploadPropertyFile, captureVideoPoster } from "../lib/media";
import { ShareDialog } from "./ShareDialog";
import {
  ChevronLeft, ChevronRight, X, Play, Upload, FileText, Download, Trash2, Star, Loader2, FileArchive, FileSpreadsheet, File as FileIcon, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

const fileIcon = (ct = "", name = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ct.includes("pdf") || ["doc", "docx", "txt"].includes(ext)) return FileText;
  if (ct.includes("sheet") || ct.includes("excel") || ["csv", "xls", "xlsx"].includes(ext)) return FileSpreadsheet;
  if (ct.includes("zip") || ext === "zip") return FileArchive;
  return FileIcon;
};

const UploadTile = ({ count, uploads, onFiles }) => {
  const { t } = useLang();
  const [drag, setDrag] = useState(false);
  const active = uploads.filter((u) => u.status === "uploading");
  return (
    <label data-testid="media-upload-tile"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(Array.from(e.dataTransfer.files || [])); }}
      className={`grid h-40 w-44 shrink-0 cursor-pointer place-items-center rounded-2xl border-2 border-dashed text-center transition-colors ${drag ? "border-brand-red bg-brand-red/5" : "border-white/15 hover:border-brand-red"}`}>
      <input data-testid="media-upload-input" type="file" accept="*/*" multiple className="hidden" onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
      <div className="px-2">
        <p className="text-[11px] text-muted-foreground">{count} ITEM{count === 1 ? "" : "S"}</p>
        {active.length ? (
          <div className="mt-1">
            <div className="flex items-center justify-center gap-1.5 text-brand-red"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm font-medium">Uploading {active.length}</span></div>
            <div className="mt-2 h-1.5 w-32 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-brand-red transition-[width]" style={{ width: `${Math.round(active.reduce((a, u) => a + u.progress, 0) / active.length)}%` }} /></div>
            <p className="mt-1 truncate text-[10px] text-muted-foreground">{active[0].name}</p>
          </div>
        ) : (
          <>
            <div className="mt-1 flex items-center justify-center gap-1.5 text-brand-red"><Upload className="h-4 w-4" /><span className="text-sm font-medium">{t("Add media")}</span></div>
            <p className="text-[10px] text-muted-foreground">photos · videos · PDFs · any file</p>
          </>
        )}
      </div>
    </label>
  );
};

// Video tile: real poster frame when we have one, else the browser's first decodable frame.
const VideoPreview = ({ item }) => {
  if (item.poster) return <img src={mediaSrc(item.poster)} alt="" data-testid="video-poster" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />;
  if (item.external) return <div className="grid h-full w-full place-items-center bg-gradient-to-br from-white/[0.06] to-black/60 text-[11px] uppercase tracking-widest text-muted-foreground">{item.provider || "video"}</div>;
  return <video src={`${mediaSrc(item.url)}#t=0.1`} preload="metadata" muted playsInline className="h-full w-full object-cover" />;
};

const Thumb = ({ item, index, isCover, onOpen, onRemove, onCover }) => (
  <div data-testid={`media-thumb-${index}`} className="group relative h-40 w-56 shrink-0 overflow-hidden rounded-2xl bg-black/40">
    <button onClick={onOpen} className="h-full w-full">
      {item.kind === "video" ? <VideoPreview item={item} /> : (
        <img src={mediaSrc(item.url)} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
      )}
      <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
      {item.kind === "video" && (
        <span className="absolute inset-0 grid place-items-center"><span className="grid h-12 w-12 place-items-center rounded-full bg-black/60 text-white backdrop-blur"><Play className="ml-0.5 h-5 w-5" fill="currentColor" /></span></span>
      )}
    </button>
    {item.kind === "video" && <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{item.external ? `${item.provider || "listing"} video` : "Video"}</span>}
    {isCover && <span className="absolute left-2 top-2 rounded-full bg-brand-red px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Cover</span>}
    <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
      {item.kind === "image" && !isCover && (
        <button data-testid={`media-cover-${index}`} title="Set as cover" onClick={onCover} className="grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white hover:bg-brand-red"><Star className="h-3.5 w-3.5" /></button>
      )}
      {item.removable && (
        <button data-testid={`media-remove-${index}`} title="Remove" onClick={onRemove} className="grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white hover:bg-brand-red"><Trash2 className="h-3.5 w-3.5" /></button>
      )}
    </div>
  </div>
);

export const LightboxMedia = ({ item, srcOf = mediaSrc }) => {
  if (item.kind === "video" && item.external && item.embed) {
    return <iframe data-testid="lightbox-embed" title={item.name || "Video"} src={item.embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen
      onClick={(e) => e.stopPropagation()} className="aspect-video w-[min(96vw,1200px)] rounded-xl border-0 bg-black" />;
  }
  if (item.kind === "video") {
    return <video data-testid="lightbox-video" key={item.url} src={srcOf(item.url)} poster={item.poster ? srcOf(item.poster) : undefined} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} className="max-h-[94vh] w-auto max-w-[96vw] rounded-xl" />;
  }
  return <LightboxImage src={srcOf(item.url)} />;
};

const LightboxImage = ({ src }) => {
  const [state, setState] = useState("loading");
  useEffect(() => { setState("loading"); }, [src]);
  return (
    <div className="relative grid place-items-center" onClick={(e) => e.stopPropagation()}>
      {state === "loading" && <div className="absolute h-10 w-10 animate-spin rounded-full border-2 border-brand-red border-t-transparent" data-testid="lightbox-loading" />}
      {state === "error" ? (
        <div data-testid="lightbox-error" className="rounded-2xl border border-white/10 bg-white/[0.04] px-8 py-10 text-center">
          <p className="text-sm text-white">This photo couldn't be loaded — the listing site may block direct access.</p>
          <a href={src} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm text-brand-green underline-offset-2 hover:underline">Open the original</a>
        </div>
      ) : (
        <img src={src} alt="" data-testid="lightbox-image" onLoad={() => setState("ok")} onError={() => setState("error")}
          className={`h-[94vh] w-auto max-w-[96vw] rounded-xl object-contain transition-opacity ${state === "ok" ? "opacity-100" : "opacity-0"}`} />
      )}
    </div>
  );
};

export const Lightbox = ({ items, index, onClose, onMove, srcOf }) => {
  const item = items[index];
  return createPortal(
    <div data-testid="media-lightbox" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <button onClick={onClose} className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
      {items.length > 1 && <button onClick={(e) => { e.stopPropagation(); onMove(-1); }} className="absolute left-4 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>}
      <LightboxMedia item={item} srcOf={srcOf} />
      {items.length > 1 && <button onClick={(e) => { e.stopPropagation(); onMove(1); }} className="absolute right-4 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>}
      <span className="absolute bottom-5 rounded-full bg-black/60 px-3 py-1 text-sm text-white">{index + 1} / {items.length}{item.name ? ` · ${item.name}` : ""}</span>
    </div>,
    document.body,
  );
};

const FilesList = ({ files, onRemove }) => {
  const { t } = useLang();
  return (
  <div data-testid="property-files" className="glass rounded-2xl p-4">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-white">{t("Files")}</h3>
      <span className="text-[11px] text-muted-foreground">{files.length} {t(files.length === 1 ? "document" : "documents")}</span>
    </div>
    <div className="divide-y divide-white/5">
      {files.map((f, i) => {
        const Icon = fileIcon(f.content_type, f.name);
        return (
          <div key={f.url} data-testid={`property-file-${i}`} className="flex items-center gap-3 py-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5 text-brand-red"><Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-white">{f.name}</p>
              <p className="text-[11px] text-muted-foreground">{formatBytes(f.size)}{f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ""}</p>
            </div>
            <a href={mediaSrc(f.url)} target="_blank" rel="noreferrer" data-testid={`property-file-open-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-white/5 hover:text-white"><Download className="h-4 w-4" /></a>
            <button onClick={() => onRemove(f.url)} data-testid={`property-file-remove-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-brand-red/15 hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        );
      })}
    </div>
  </div>
);
};

export const PropertyMedia = ({ property: p, onChange, onPatch }) => {
  const { t } = useLang();
  const [lightbox, setLightbox] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const pRef = useRef(p); pRef.current = p;

  const photos = (p.photos && p.photos.length ? p.photos : (p.image ? [p.image] : [])).map((url) => ({ url, kind: "image", removable: !!(p.photos && p.photos.length) }));
  const videos = (p.videos || []).map((v) => ({ ...v, kind: "video", removable: true }));
  const items = [...videos, ...photos];
  const files = p.files || [];

  const move = useCallback((d) => setLightbox((i) => (i === null ? i : (i + d + items.length) % items.length)), [items.length]);
  useEffect(() => {
    if (lightbox === null) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const h = (e) => { if (e.key === "Escape") setLightbox(null); if (e.key === "ArrowRight") move(1); if (e.key === "ArrowLeft") move(-1); };
    window.addEventListener("keydown", h);
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = prevOverflow; };
  }, [lightbox, move]);

  const setProgress = (key, patch) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const onFiles = async (list) => {
    if (!list.length) return;
    const tooBig = list.filter((f) => f.size > 1024 * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.length} file(s) over 1 GB skipped`);
    const queue = list.filter((f) => f.size <= 1024 * 1024 * 1024).map((f) => ({ key: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`, file: f, name: f.name, kind: mediaKind(f), progress: 0, status: "uploading" }));
    setUploads((u) => [...u, ...queue]);
    let ok = 0;
    for (const q of queue) {
      try {
        const posterPromise = q.kind === "video" ? captureVideoPoster(q.file) : Promise.resolve(null);
        const res = await uploadPropertyFile(q.file, pRef.current.id, (pct) => setProgress(q.key, { progress: pct }));
        onChange(res.property);
        const poster = await posterPromise;
        if (poster) {
          const pf = new File([poster], `${q.name.replace(/\.[^.]+$/, "")}-poster.jpg`, { type: "image/jpeg" });
          const pr = await uploadPropertyFile(pf, pRef.current.id, null, { poster_for: res.media.id });
          onChange(pr.property);
        }
        ok++;
        setProgress(q.key, { status: "done", progress: 100 });
      } catch (e) {
        setProgress(q.key, { status: "error" });
        toast.error(`Upload failed: ${q.name}`, { description: e?.response?.data?.detail || e.message });
      }
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    setTimeout(() => setUploads((u) => u.filter((x) => x.status === "uploading")), 1500);
  };

  const remove = async (url) => {
    try {
      const r = await client.post(`/properties/${p.id}/media/remove`, { url });
      onChange(r.data); toast.success("Removed");
    } catch (e) { toast.error("Couldn't remove", { description: e?.response?.data?.detail }); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("Gallery")} · {videos.length} video{videos.length === 1 ? "" : "s"} · {photos.length} photo{photos.length === 1 ? "" : "s"}{files.length ? ` · ${files.length} file${files.length === 1 ? "" : "s"}` : ""}</p>
        <button data-testid="share-media-btn" onClick={() => setShareOpen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/12 bg-white/[0.03] px-3 text-sm text-white transition-colors hover:bg-white/[0.07]">
          <Link2 className="h-4 w-4 text-brand-green" /> {t("Share link")}
        </button>
      </div>
      <div data-testid="property-gallery" className="flex gap-3 overflow-x-auto no-scrollbar">
        <UploadTile count={items.length + files.length} uploads={uploads} onFiles={onFiles} />
        {items.map((item, i) => (
          <Thumb key={item.url} item={item} index={i} isCover={item.kind === "image" && p.image === item.url}
            onOpen={() => setLightbox(i)} onRemove={() => remove(item.url)} onCover={() => onPatch({ image: item.url })} />
        ))}
      </div>
      {files.length > 0 && <FilesList files={files} onRemove={remove} />}
      {lightbox !== null && items[lightbox] && <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onMove={move} />}
      {shareOpen && <ShareDialog property={p} items={items} files={files} onClose={() => setShareOpen(false)} />}
    </>
  );
};
